import './StaffSettingsSection.css'

import { useEffect, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { FilterBar } from '../../../components/FilterBar'
import { PageHeader } from '../../../components/PageHeader'
import { SegmentedControl } from '../../../components/SegmentedControl'
import { Tag } from '../../../components/Tag'

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'notified', label: 'Notified' },
  { value: 'converted', label: 'Converted' },
  { value: 'all', label: 'All' },
]

export function StaffSettingsWaitlistView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const entries = useAtomValue(staff.staffWaitlistAtom)
  const total = useAtomValue(staff.staffWaitlistTotalAtom)
  const status = useAtomValue(staff.staffWaitlistStatusAtom)
  const isLoading = useAtomValue(staff.staffWaitlistIsLoadingAtom)
  const error = useAtomValue(staff.staffWaitlistErrorAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const setStatus = useSetAtom(staff.staffWaitlistStatusAtom)
  const fetchWaitlist = useSetAtom(staff.fetchStaffWaitlistAtom)
  const notifyEntry = useSetAtom(staff.notifyStaffWaitlistAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.SettingsWaitlistRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.SettingsWaitlistWrite)

  useEffect(() => {
    if (canRead) fetchWaitlist()
  }, [canRead, fetchWaitlist, status])

  const columns = useMemo(
    () => [
      {
        id: 'email',
        header: 'Email',
        render: (entry: staff.StaffWaitlistEntry) => entry.email,
      },
      {
        id: 'location',
        header: 'Location',
        render: (entry: staff.StaffWaitlistEntry) =>
          `${entry.location || '—'}${entry.country ? ` (${entry.country})` : ''}`,
      },
      {
        id: 'status',
        header: 'Status',
        render: (entry: staff.StaffWaitlistEntry) => {
          if (entry.convertedAt) return <Tag color="teal">converted</Tag>
          if (entry.notifiedAt) return <Tag color="violet">notified</Tag>
          return <Tag color="amber">pending</Tag>
        },
      },
      {
        id: 'created',
        header: 'Joined',
        render: (entry: staff.StaffWaitlistEntry) => formatDate(entry.createdAt),
      },
      {
        id: 'actions',
        header: '',
        render: (entry: staff.StaffWaitlistEntry) =>
          canWrite && !entry.convertedAt ? (
            <Button
              kind="secondary"
              size="small"
              text={entry.notifiedAt ? 'Re-notify' : 'Notify'}
              disabled={actionLoading}
              onClick={() => notifyEntry(entry.id)}
            />
          ) : null,
      },
    ],
    [canWrite, actionLoading, notifyEntry],
  )

  if (!canRead) {
    return (
      <EmptyState
        title="No waitlist access"
        description="Your role does not include settings.waitlist.read."
      />
    )
  }

  return (
    <div className="staff-settings-section">
      <PageHeader
        title="Waitlist"
        subtitle="Pending, notified, and converted region waitlist entries."
      />

      <FilterBar>
        <SegmentedControl
          ariaLabel="Waitlist status"
          value={status}
          onChange={setStatus}
          options={statusOptions}
        />
        <Button
          kind="secondary"
          text={isLoading ? 'Loading…' : 'Refresh'}
          onClick={() => fetchWaitlist()}
          disabled={isLoading}
        />
      </FilterBar>

      <p className="staff-settings-section__meta">
        {isLoading ? 'Loading…' : `${total.toLocaleString()} entr${total === 1 ? 'y' : 'ies'}`}
      </p>

      {error && (
        <EmptyState
          title="Failed to load waitlist"
          description={error === 'forbidden' ? 'Forbidden.' : 'Request failed.'}
        />
      )}

      {!error && (
        <DataTable
          columns={columns}
          rows={entries}
          getRowKey={(entry) => entry.id}
          emptyMessage="No waitlist entries match"
        />
      )}
    </div>
  )
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
